import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Bell,
  ArrowLeft,
  Home,
  LogOut,
  Calendar,
  User,
  Shield,
  IndianRupee,
  Phone,
  ChevronRight,
  Clock,
  AlertTriangle,
  Menu,
  RefreshCw
} from "lucide-react";
import { authGet, getAuthToken, logout } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorRenewals() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [renewals, setRenewals] = useState([]);
  const [daysFilter, setDaysFilter] = useState("30");
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    // Check auth via token
    const checkAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        navigate("/advisor/login");
        return;
      }
      try {
        const response = await authGet('/api/auth/me');
        if (!response.ok) {
          navigate("/advisor/login");
          return;
        }
        fetchRenewals(daysFilter);
      } catch {
        navigate("/advisor/login");
      }
    };
    checkAuth();
  }, [navigate, daysFilter]);

  const fetchRenewals = async (days) => {
    setLoading(true);
    
    try {
      const response = await authGet(`/api/advisor/renewals?days=${days}`);
      
      if (response.status === 401) {
        navigate("/advisor/login");
        return;
      }
      
      setRenewals(response.data?.renewals || []);
    } catch (error) {
      console.error("Error fetching renewals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntilRenewal = (endDate) => {
    const end = new Date(endDate);
    const today = new Date();
    return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  };

  const getUrgencyColor = (days) => {
    if (days <= 7) return "red";
    if (days <= 15) return "amber";
    return "emerald";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden bg-white/95 backdrop-blur-xl sticky top-0 z-40 safe-area-inset-top border-b border-[#C9D4E6]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowSidebar(true)}
              className="p-2 -ml-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] active:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <Bell className="h-5 w-5 text-[#0D1A3A]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1B2A4A]">Renewals</h1>
              <p className="text-xs text-[#3A4557]">{renewals.length} upcoming</p>
            </div>
          </div>
          
          {/* Mobile Filter */}
          <Select value={daysFilter} onValueChange={setDaysFilter}>
            <SelectTrigger className="w-[90px] h-9 bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] text-xs rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#C9D4E6]">
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="15">15 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="60">60 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block bg-[#EDF1F8]/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/advisor/dashboard")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                <Bell className="h-5 w-5 text-[#0D1A3A]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#1B2A4A]">Upcoming Renewals</h1>
                <p className="text-[#3A4557] text-sm">{renewals.length} policies due</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <Home className="h-4 w-4 mr-2" />
                Website
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 md:py-8">
        {/* Desktop Filter */}
        <div className="hidden md:flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <span className="text-[#3A4557]">Show renewals in next:</span>
            <Select value={daysFilter} onValueChange={setDaysFilter}>
              <SelectTrigger className="w-[140px] bg-white/50 border-[#C9D4E6] text-[#1B2A4A]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#C9D4E6]">
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="15">15 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Renewals List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="h-12 w-12 border-4 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#3A4557]">Loading renewals...</p>
          </div>
        ) : renewals.length === 0 ? (
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-2xl p-8 md:p-12 text-center">
            <Calendar className="h-16 w-16 mx-auto mb-4 text-[#3A4557]" />
            <h3 className="text-xl font-semibold text-[#1B2A4A] mb-2">No Upcoming Renewals</h3>
            <p className="text-[#3A4557]">
              No policies are due for renewal in the next {daysFilter} days.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {renewals.map((policy) => {
              const daysLeft = getDaysUntilRenewal(policy.policy_end_date);
              const urgencyColor = getUrgencyColor(daysLeft);
              
              return (
                <div
                  key={policy.id}
                  onClick={() => navigate(`/advisor/clients/${policy.client_id}`)}
                  className="w-full bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 hover:border-[#D4A017]/50 rounded-2xl p-4 transition-all active:scale-[0.98] text-left cursor-pointer"
                  data-testid={`renewal-card-${policy.id}`}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Urgency Indicator */}
                    <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-xl ${
                      urgencyColor === 'red' ? 'bg-red-500/20' : 
                      urgencyColor === 'amber' ? 'bg-[#D4A017]/15' : 'bg-emerald-500/20'
                    } flex flex-col items-center justify-center flex-shrink-0`}>
                      <span className={`text-xl sm:text-2xl font-bold ${
                        urgencyColor === 'red' ? 'text-red-600' : 
                        urgencyColor === 'amber' ? 'text-[#A87608]' : 'text-emerald-600'
                      }`}>{daysLeft}</span>
                      <span className={`text-[10px] ${
                        urgencyColor === 'red' ? 'text-red-600' : 
                        urgencyColor === 'amber' ? 'text-[#A87608]' : 'text-emerald-600'
                      }`}>days</span>
                    </div>
                    
                    {/* Policy & Client Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="text-base font-semibold text-[#1B2A4A] truncate">{policy.client?.name || "Unknown Client"}</h3>
                          <p className="text-[#3A4557] text-sm truncate">{policy.plan_name}</p>
                        </div>
                        {daysLeft <= 7 && (
                          <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-red-500/20 text-red-600 flex items-center gap-1 flex-shrink-0">
                            <AlertTriangle className="h-3 w-3" />
                            Urgent
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#3A4557] mt-2">
                        <span className="text-[#A87608] font-medium">{policy.insurance_company}</span>
                        <span className="flex items-center gap-1">
                          <IndianRupee className="h-3 w-3" />
                          {policy.premium_amount?.toLocaleString('en-IN')}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        <Clock className="h-3 w-3 text-[#3A4557]" />
                        <span className={`font-medium ${
                          urgencyColor === 'red' ? 'text-red-600' : 
                          urgencyColor === 'amber' ? 'text-[#A87608]' : 'text-emerald-600'
                        }`}>{formatDate(policy.policy_end_date)}</span>
                      </div>
                    </div>
                    
                    <ChevronRight className="h-5 w-5 text-[#3A4557] flex-shrink-0 self-center hidden sm:block" />
                  </div>
                  
                  <div className="flex items-center justify-end mt-3 pt-3 border-t border-[#C9D4E6]/50">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/advisor/map-policy?renewalOf=${policy.id}`);
                      }}
                      className="bg-[#D4A017]/15 text-[#A87608] hover:bg-[#D4A017]/25 border border-[#D4A017]/40 rounded-xl h-8"
                      data-testid={`map-renewed-policy-btn-${policy.id}`}
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      Map Renewed Policy
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
